// ---------------------------------------------------------------------------
// Members guides - the first real customer, and the first real sale.
//
// Everything in setup.js builds capacity. These two are where the capacity
// turns into a person sitting at a desk and an amount owed. Same do-it-live
// rules as the setup lane: real controls, real saves, nothing simulated.
// ---------------------------------------------------------------------------

import { PATH_ADMIN } from '../../routes/paths';
import { ANCHOR, CENTER } from '../anchors';
import { COACH_EVENT } from '../events';

const MEMBERSHIPS_HOME = PATH_ADMIN.members.membershipsPage;
const ORDERS = PATH_ADMIN.orderManagement.list;
const CREATE_ORDER = PATH_ADMIN.orderManagement.create;

export const MEMBERS_FIRST_MEMBER = {
  id: 'members-first-member',
  title: 'Add your first member',
  summary:
    'Get a real person into the system, attached to the company that pays for '
    + 'them, with their own login to your portal and app.',
  category: 'setup',
  level: 'advanced',
  permission: ['members.view', 'members.create'],
  featureSlug: null,
  estMinutes: 5,
  readinessKey: 'first_member',
  keywords: [
    'member', 'add member', 'customer', 'company', 'client', 'tenant',
    'onboard member', 'new member',
  ],
  steps: [
    {
      id: 'intro',
      kind: 'narrate',
      route: MEMBERSHIPS_HOME,
      target: CENTER,
      placement: 'center',
      title: 'Members are the people who pay you',
      body:
        'Your own staff live under Team Members and never appear here. This side '
        + 'is your customers. Two things sit behind every one of them: a company, '
        + 'which is who gets invoiced, and a member, which is a person who logs in '
        + 'and walks through your door. Even a freelancer taking one desk gets a '
        + 'company, because the invoice has to be addressed to somebody.',
    },
    {
      id: 'open-menu',
      kind: 'act',
      route: MEMBERSHIPS_HOME,
      target: ANCHOR.membersAddNew,
      placement: 'bottom',
      title: 'Start here',
      body:
        'This menu is where every kind of new customer record starts, whether you '
        + 'are adding one person or a whole company at once.',
      waitingLabel: 'Click Add New.',
      advanceOn: [{ type: 'click', target: ANCHOR.membersAddNew }],
      doItForMe: { type: 'click', target: ANCHOR.membersAddNew },
      allowSkip: true,
    },
    {
      id: 'pick-member',
      kind: 'act',
      route: MEMBERSHIPS_HOME,
      target: ANCHOR.membersAddMember,
      placement: 'right',
      title: 'Add a member',
      body:
        'You do not need to create the company first. The member form can create '
        + 'one as you go, which is the shorter road for most operators.',
      waitingLabel: 'Click Add Member.',
      advanceOn: [{ type: 'click', target: ANCHOR.membersAddMember }],
      doItForMe: { type: 'click', target: ANCHOR.membersAddMember },
      allowSkip: true,
    },
    {
      id: 'describe',
      kind: 'narrate',
      route: PATH_ADMIN.members.add,
      target: CENTER,
      placement: 'center',
      title: 'Who they are, and who they belong to',
      body:
        'Name, email and phone are the person. The email is their login and where '
        + 'invoices and notices are sent, so it has to be one they read. Below '
        + 'that you either pick an existing company or create a new one. Pick the '
        + 'company carefully: it decides who the bill goes to and what the person '
        + 'can see about their team.',
    },
    {
      id: 'save',
      kind: 'await',
      route: PATH_ADMIN.members.add,
      target: CENTER,
      placement: 'center',
      title: 'Save them',
      body:
        'If you mark them a company admin, they can see and manage the rest of '
        + 'their team in the portal, which saves you being asked to add people one '
        + 'at a time. Save when the details are right.',
      waitingLabel: 'Waiting for the member to be created.',
      advanceOn: [{ type: 'event', name: COACH_EVENT.memberCreated }],
      allowSkip: true,
    },
    {
      id: 'done',
      kind: 'narrate',
      route: MEMBERSHIPS_HOME,
      target: CENTER,
      placement: 'center',
      title: 'They exist, but they do not have a desk yet',
      body:
        'A member on its own has no seat, no plan and no invoice. Nothing is owed '
        + 'and nothing is reserved. What turns them into a paying customer is a '
        + 'subscription, which is the next guide. Bulk import lives in the same '
        + 'Add New menu if you are migrating a full house rather than adding one '
        + 'person.',
    },
  ],
};

export const MEMBERS_FIRST_SUBSCRIPTION = {
  id: 'members-first-subscription',
  title: 'Sell your first membership',
  summary:
    'Put a company on a plan, reserve the actual seats they will sit in, and let '
    + 'DeskOS raise the invoice for it.',
  category: 'setup',
  level: 'advanced',
  // Selling a subscription happens in order management, not in the
  // subscriptions list, so this guide crosses into the billing codes.
  permission: ['subscriptions.manage', 'billing.view', 'billing.create'],
  featureSlug: null,
  estMinutes: 8,
  readinessKey: 'first_subscription',
  keywords: [
    'subscription', 'membership', 'sell', 'order', 'new order', 'assign seat',
    'monthly plan', 'book a desk',
  ],
  steps: [
    {
      id: 'intro',
      kind: 'narrate',
      route: ORDERS,
      target: CENTER,
      placement: 'center',
      title: 'A subscription is the deal itself',
      body:
        'It ties four things together: the company being billed, the plan that '
        + 'sets the price, the exact seats being held for them, and the period it '
        + 'runs for. Once it exists, those seats read as occupied everywhere in '
        + 'DeskOS and the invoice is raised off the same record. This is the '
        + 'single most important object in the system, so it is worth doing one by '
        + 'hand before you do fifty.',
    },
    {
      id: 'new-order',
      kind: 'act',
      route: ORDERS,
      target: ANCHOR.ordersNewOrder,
      placement: 'bottom',
      title: 'Start a new order',
      body:
        'Everything you sell starts from this button: monthly memberships, leases, '
        + 'day passes and one-off meeting-room bookings.',
      waitingLabel: 'Click New Order.',
      advanceOn: [{ type: 'click', target: ANCHOR.ordersNewOrder }],
      doItForMe: { type: 'click', target: ANCHOR.ordersNewOrder },
      allowSkip: true,
    },
    {
      id: 'pick-monthly',
      kind: 'act',
      route: ORDERS,
      target: ANCHOR.orderTypeMonthly,
      placement: 'bottom',
      title: 'Monthly plan',
      body:
        'This is the recurring membership, the one that renews and bills every '
        + 'period. A lease is for long fixed-term deals with deposits and '
        + 'escalation, and a day pass is for someone walking in tomorrow.',
      waitingLabel: 'Click Monthly Plan.',
      advanceOn: [{ type: 'click', target: ANCHOR.orderTypeMonthly }],
      doItForMe: { type: 'click', target: ANCHOR.orderTypeMonthly },
      allowSkip: true,
    },
    {
      id: 'company-and-plan',
      kind: 'narrate',
      route: CREATE_ORDER,
      target: CENTER,
      placement: 'center',
      title: 'Company first, then the plan',
      body:
        'Pick the company you are billing. If they are not there yet, you can '
        + 'create them from here without losing this order. Then choose the plan, '
        + 'which fills in the per-seat price for you. That price is a starting '
        + 'point, not a rule, so override it on this deal if you negotiated '
        + 'something else. Anything that is not seat rent, like parking or a '
        + 'printing allowance, goes in as an additional charge rather than being '
        + 'buried in the seat price.',
    },
    {
      id: 'seats-and-dates',
      kind: 'narrate',
      route: CREATE_ORDER,
      target: CENTER,
      placement: 'center',
      title: 'Then the seats and the dates',
      body:
        'Choose the real seats they will sit in, not just a count. That is what '
        + 'makes your floor plan and Live Status honest, and it is what stops the '
        + 'same desk being sold twice. The start date drives the billing period, '
        + 'and a mid-month start is prorated for you. Only seats that are free for '
        + 'this period are offered, so an empty list usually means the dates '
        + 'overlap something already sold.',
    },
    {
      id: 'save',
      kind: 'await',
      target: CENTER,
      placement: 'center',
      title: 'Create the subscription',
      body:
        'The last step shows you the full amount with tax before anything is '
        + 'committed. Read it once. When you create it, the seats are reserved and '
        + 'the invoice for the first period is raised in the same action.',
      waitingLabel: 'Waiting for the subscription to be created.',
      advanceOn: [{ type: 'event', name: COACH_EVENT.subscriptionCreated }],
      allowSkip: true,
    },
    {
      id: 'done',
      kind: 'narrate',
      route: ORDERS,
      target: CENTER,
      placement: 'center',
      title: 'You have a paying customer',
      body:
        'The seats are occupied, the member can log in and book rooms against '
        + 'whatever credits the plan bundles, and there is an invoice waiting to '
        + 'be sent. Renewals are handled separately under Memberships, where you '
        + 'see what is expiring and can raise the next period in a batch. Next '
        + 'guide: the invoice this just produced.',
    },
  ],
};

export default [MEMBERS_FIRST_MEMBER, MEMBERS_FIRST_SUBSCRIPTION];
